import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiRequestError } from '../lib/api-client';
import { t } from '../lib/i18n';
import { AuthLayout } from './AuthLayout';
import { useAuth } from './auth-store';
import { AlertIcon, SpinnerIcon } from './icons';

/**
 * Where an e-mail sign-in link lands.
 *
 * The token arrives in the query string, which is the one thing about magic links that is awkward:
 * URLs end up in history and in `Referer` headers. So the token is read once, exchanged over POST,
 * and then removed from the address bar with a replacing navigation, leaving nothing behind for a
 * later page to leak.
 */

const MESSAGES: Record<string, string> = {
  'auth.invalid_token': 'magic.invalid',
  'auth.token_already_used': 'magic.used',
  'auth.token_expired': 'magic.expired',
  'auth.token_address_mismatch': 'magic.mismatch',
  'auth.account_disabled': 'magic.disabled',
};

export function MagicLinkPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithMagicLink } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const token = params.get('token');

  // React runs effects twice in development; the token is single use, so the second run would
  // consume nothing and report the link as already used.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setError(t('magic.invalid'));
      return;
    }

    void (async () => {
      try {
        await loginWithMagicLink(token);

        // `replace`, so the token is not in the history entry the back button returns to.
        navigate('/', { replace: true });
      } catch (caught) {
        const key = caught instanceof ApiRequestError ? MESSAGES[caught.code] : undefined;

        setError(t(key ?? 'error.generic'));
      }
    })();
  }, [token, loginWithMagicLink, navigate]);

  if (error) {
    return (
      <AuthLayout>
        <div className="auth__done">
          <span className="auth__done-mark auth__done-mark--alert" aria-hidden="true">
            <AlertIcon />
          </span>

          <h1>{t('magic.failedTitle')}</h1>
          <p>{error}</p>

          <Link className="button button--primary" to="/entrar">
            {t('magic.requestAnother')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="auth__done" aria-busy="true">
        <span className="auth__done-mark" aria-hidden="true">
          <SpinnerIcon />
        </span>

        <h1>{t('magic.checkingTitle')}</h1>
        <p role="status">{t('magic.checking')}</p>
      </div>
    </AuthLayout>
  );
}
